/**
 * What the do-not-call screen does to the list before drawing it.
 *
 * React-free so the two rules worth getting right can be tested without a DOM:
 * how a typed number is matched against a stored one, and how loudly each
 * source reads. The list arrives whole from the server (it is measured in
 * dozens - it only grows when somebody asks us to stop), so searching and
 * narrowing happen here rather than in a query.
 */
import type { PhoneSuppression } from "../../../../lib/callSuppression";
import type { StatusTone } from "../../../ui";

/** The sources the calls side can produce, in the order the filter offers them. */
export const DO_NOT_CALL_SOURCES = ["unsubscribe", "complaint", "manual", "import"] as const;

export type DoNotCallSource = (typeof DO_NOT_CALL_SOURCES)[number];

/** The filter value meaning "do not narrow by this". */
export const ANY_SOURCE = "__any__";

/**
 * How loudly each source reads.
 *
 * The same vocabulary the mail side's suppression list uses, and for the same
 * reason: a complaint is the strongest thing on the screen - somebody reported
 * us - so it takes the refusal tone, while an unsubscribe is the person's own
 * decision and equally binding but an ordinary outcome rather than a fault. A
 * hand-added row is ours and says nothing about them.
 */
const SOURCE_TONE: Record<string, StatusTone> = {
  complaint: "lt",
  unsubscribe: "wt",
  manual: "info",
  import: "mut",
};

export function sourceTone(source: string): StatusTone {
  return SOURCE_TONE[source] ?? "mut";
}

/**
 * The digits of a number, for comparing one against another.
 *
 * Both sides go through this, which is the whole point: the stored value is
 * always E.164 (`+37255512345`) and an operator searching for it types
 * whatever is in front of them - "5551 2345" off a business card, "+372 5551
 * 2345" out of the call log, "0037255512345" pasted from a spreadsheet. Matching
 * the strings would find none of those; matching the digits finds all of them,
 * because a stored number ENDS with whatever local part was typed.
 *
 * THE LEADING `00` IS DROPPED. It and the `+` are the same thing said two ways -
 * both mean "a country code follows" - but only the `+` is punctuation, so
 * stripping non-digits leaves the `00` behind and the search then looks for
 * `0037255512345` inside `37255512345` and finds nothing. The symptom is the
 * most dangerous wrong answer this screen can give: "that number is not on the
 * list", about a number that is, with somebody about to ring it.
 *
 * Only ever the leading pair, so a real number containing `00` is untouched.
 */
export function phoneDigits(raw: string): string {
  return raw.replace(/\D+/g, "").replace(/^00/, "");
}

/** True when a typed query looks like somebody is searching for a number. */
function looksNumeric(q: string): boolean {
  return /\d/.test(q);
}

export interface DoNotCallFilter {
  q: string;
  /** One source, or `ANY_SOURCE`. */
  source: string;
}

export const DEFAULT_DO_NOT_CALL_FILTER: DoNotCallFilter = { q: "", source: ANY_SOURCE };

export function doNotCallFilterActive(f: DoNotCallFilter): boolean {
  return f.q.trim() !== "" || f.source !== ANY_SOURCE;
}

/**
 * One row's searchable text, minus the number.
 *
 * The note and the reason are in it because that is where an operator writes
 * WHY ("said at the trade fair", "wrong company"), and the operator's own name
 * is in it because "who put this on the list" is a real question about a list
 * nobody can take entries off again.
 */
function textOf(row: PhoneSuppression): string {
  return [row.party?.companyName, row.note, row.reason, row.createdByName]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Does this row answer the query? */
export function matchesQuery(row: PhoneSuppression, q: string): boolean {
  const query = q.trim();
  if (!query) return true;

  if (looksNumeric(query)) {
    const digits = phoneDigits(query);
    // `includes`, not `endsWith`: a country code typed in front is a prefix and
    // a local number typed alone is a suffix, and both are the same search.
    if (digits && phoneDigits(row.value).includes(digits)) return true;
  }

  return textOf(row).includes(query.toLowerCase());
}

/** The rows to draw, in the order the server sent them (newest first). */
export function filterDoNotCall(rows: PhoneSuppression[], f: DoNotCallFilter): PhoneSuppression[] {
  return rows.filter((row) => {
    if (f.source !== ANY_SOURCE && row.source !== f.source) return false;
    return matchesQuery(row, f.q);
  });
}

/** How many of these entries nobody here may take off again. Drawn above the
 *  list, because it is the number that says how much of it is not ours. */
export function permanentCount(rows: PhoneSuppression[]): number {
  return rows.filter((r) => r.permanent).length;
}
